import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { Send, Plus, Trash2, Mail, AlertTriangle, Check, X, Copy } from 'lucide-react';

const Chat = () => {
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [emailProvider, setEmailProvider] = useState(null);
  const [emailProviderCode, setEmailProviderCode] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const response = await api.get('/chat/conversations');
      setConversations(response.data.conversations);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const response = await api.get(`/chat/conversations/${conversationId}`);
      setCurrentConversation(response.data.conversation);
      setMessages(response.data.messages || []);
      setPendingEmail(null);
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  };

  const startNewConversation = () => {
    setCurrentConversation(null);
    setMessages([]);
    setPendingEmail(null);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;

    const userMessage = input.trim();
    setInput('');
    setSending(true);
    setPendingEmail(null);

    // Add user message to UI immediately
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);

    try {
      const response = await api.post('/chat', {
        message: userMessage,
        conversationId: currentConversation?.conversation_id
      });

      // Add assistant response
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.message }]);

      // Check if this is an email response (single PO or batch)
      if (response.data.emailData) {
        setPendingEmail(response.data.emailData);
        setEmailConfigured(response.data.emailConfigured);
        setEmailProvider(response.data.providerName);
        setEmailProviderCode(response.data.providerCode);
      } else if (response.data.batchResult?.emails?.length > 0) {
        // Batch processing returned emails for confirmation
        setPendingEmail({
          poNumber: 'Batch',
          lineCount: response.data.batchResult.emails.length,
          emails: response.data.batchResult.emails,
          skippedLines: response.data.batchResult.skippedLines || []
        });
        setEmailConfigured(response.data.emailConfigured);
        setEmailProvider(response.data.providerName);
        setEmailProviderCode(response.data.providerCode);
      }

      // Update conversation ID if new
      if (!currentConversation) {
        setCurrentConversation({ conversation_id: response.data.conversationId });
        fetchConversations();
      }
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.' 
      }]);
    } finally {
      setSending(false);
    }
  };

  // Confirm emails have been sent (manually or via API)
  const handleConfirmSent = async (sendVia = 'manual') => {
    if (!pendingEmail || sendingEmail) return;
    
    setSendingEmail(true);
    try {
      const response = await api.post('/chat/confirm', {
        conversationId: currentConversation?.conversation_id,
        emailData: pendingEmail,
        sendVia
      });
      
      const count = response.data.results?.sent?.length || 0;
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `✅ ${count} email${count !== 1 ? 's' : ''} confirmed. Status updated to DAY1_SENT.` 
      }]);
      setPendingEmail(null);
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `❌ Failed to confirm: ${err.response?.data?.error || 'Unknown error'}` 
      }]);
    } finally {
      setSendingEmail(false);
    }
  };

  // Send via API (if configured)
  const handleSendViaAPI = async () => {
    if (!pendingEmail || sendingEmail) return;
    
    setSendingEmail(true);
    try {
      const response = await api.post('/chat/confirm', {
        conversationId: currentConversation?.conversation_id,
        emailData: pendingEmail,
        sendVia: emailProviderCode || 'gmail'
      });
      
      const results = response.data.results || {};
      const sentCount = results.sent?.length || 0;
      const failedCount = results.failed?.length || 0;
      
      let msg = `✅ ${sentCount} email${sentCount !== 1 ? 's' : ''} sent via ${emailProvider || 'Email'}.`;
      if (failedCount > 0) {
        msg += ` ${failedCount} failed.`;
      }
      msg += ' Status updated to DAY1_SENT.';
      
      setMessages(prev => [...prev, { role: 'assistant', content: msg }]);
      setPendingEmail(null);
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `❌ Failed to send: ${err.response?.data?.error || 'Unknown error'}` 
      }]);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCancelEmail = () => {
    setMessages(prev => [...prev, { 
      role: 'assistant', 
      content: '📧 Email cancelled. The draft has been discarded.' 
    }]);
    setPendingEmail(null);
  };

  const handleCopyEmail = async () => {
    if (!pendingEmail) return;
    
    let emailText = '';
    
    if (pendingEmail.emails) {
      // Multi-line: copy all emails
      for (const email of pendingEmail.emails) {
        emailText += `--- Line ${email.lineNumber} ---\n`;
        emailText += `To: ${email.to}\nSubject: ${email.subject}\n\n${email.body}\n\n`;
      }
    } else {
      // Single email
      emailText = `To: ${pendingEmail.to}\nSubject: ${pendingEmail.subject}\n\n${pendingEmail.body}`;
    }
    
    try {
      await navigator.clipboard.writeText(emailText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const deleteConversation = async (conversationId) => {
    if (!window.confirm('Delete this conversation?')) return;
    
    try {
      await api.delete(`/chat/conversations/${conversationId}`);
      fetchConversations();
      if (currentConversation?.conversation_id === conversationId) {
        startNewConversation();
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '24px', height: 'calc(100vh - 140px)' }}>
      {/* Sidebar */}
      <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
        <div className="card-header">
          <span className="card-title">Conversations</span>
          <button className="btn btn-primary btn-sm" onClick={startNewConversation}>
            <Plus size={14} />
          </button>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          {conversations.length === 0 ? (
            <div className="empty-state" style={{ padding: '20px' }}>
              <p className="text-sm">No conversations yet</p>
            </div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.conversation_id}
                onClick={() => loadConversation(conv.conversation_id)}
                style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid var(--border)',
                  cursor: 'pointer',
                  background: currentConversation?.conversation_id === conv.conversation_id ? 'var(--bg-secondary)' : 'transparent',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="text-sm font-medium" style={{ 
                    whiteSpace: 'nowrap', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis' 
                  }}>
                    {conv.title || 'New conversation'}
                  </div>
                  <div className="text-sm text-muted">
                    {new Date(conv.updated_at).toLocaleDateString()}
                  </div>
                </div>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteConversation(conv.conversation_id);
                  }}
                  style={{ padding: '4px' }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="chat-container">
        <div className="chat-messages">
          {messages.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: '3rem', marginBottom: '16px' }}>💬</div>
              <h3>Chat Assistant</h3>
              <p className="text-sm text-muted" style={{ maxWidth: '400px', margin: '12px auto' }}>
                Ask questions about your PO data, get help with expediting, or request information using natural language.
              </p>
              <div style={{ marginTop: '24px' }}>
                <p className="text-sm text-muted mb-4">Try asking:</p>
                <div className="flex flex-col gap-2">
                  {[
                    "Show me all past due POs",
                    "Process PO G1340836",
                    "Get supplier contact for PO 12345"
                  ].map((suggestion) => (
                    <button
                      key={suggestion}
                      className="btn btn-outline btn-sm"
                      onClick={() => setInput(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div key={idx} className={`chat-message ${msg.role}`}>
                <div className="message-bubble">
                  {msg.role === 'assistant' ? (
                    <div dangerouslySetInnerHTML={{ __html: msg.content.replace(/\n/g, '<br>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))
          )}
          
          {/* Email Confirmation Panel */}
          {pendingEmail && (
            <div className="chat-message assistant">
              <div className="message-bubble" style={{ padding: 0, overflow: 'hidden' }}>
                {/* Header */}
                <div style={{ 
                  background: 'var(--primary)', 
                  color: 'white', 
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Mail size={18} />
                  <strong>
                    {pendingEmail.emails 
                      ? (pendingEmail.poNumber === 'Batch' || pendingEmail.poNumber === 'Pending')
                        ? `Send ${pendingEmail.lineCount} email${pendingEmail.lineCount > 1 ? 's' : ''} (${pendingEmail.poNumber === 'Pending' ? 'Pending Emails' : 'Batch Processing'})?`
                        : `Send ${pendingEmail.lineCount} email${pendingEmail.lineCount > 1 ? 's' : ''} for PO ${pendingEmail.poNumber}?`
                      : 'Send this email?'
                    }
                  </strong>
                </div>
                
                {/* Skipped Lines Warning */}
                {pendingEmail.skippedLines?.length > 0 && (
                  <div style={{ padding: '12px 16px', background: '#fff3cd', borderBottom: '1px solid #ffc107' }}>
                    <div className="text-sm" style={{ color: '#856404', fontWeight: 600 }}>
                      ⚠️ {pendingEmail.skippedLines.length} item(s) skipped - missing supplier email:
                    </div>
                    <div className="text-sm" style={{ color: '#856404', marginTop: '4px' }}>
                      {pendingEmail.skippedLines.slice(0, 5).map((s, i) => (
                        <div key={i}>• PO {s.poNumber} Line {s.lineNumber}: {s.supplier}</div>
                      ))}
                      {pendingEmail.skippedLines.length > 5 && (
                        <div>...and {pendingEmail.skippedLines.length - 5} more</div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Email Preview */}
                <div style={{ padding: '16px', maxHeight: '300px', overflow: 'auto' }}>
                  {pendingEmail.emails ? (
                    // Multi-line PO or batch - show summary of each email
                    pendingEmail.emails.map((email, idx) => (
                      <div key={idx} style={{ 
                        marginBottom: idx < pendingEmail.emails.length - 1 ? '12px' : 0,
                        padding: '12px',
                        background: 'var(--bg-secondary)',
                        borderRadius: 'var(--radius)'
                      }}>
                        <div className="text-sm" style={{ fontWeight: 600, marginBottom: '4px' }}>
                          {(pendingEmail.poNumber === 'Batch' || pendingEmail.poNumber === 'Pending')
                            ? `PO ${email.poNumber} Line ${email.lineNumber}: ${email.itemNumber || ''}`
                            : `Line ${email.lineNumber}: ${email.itemNumber || ''}`
                          }
                        </div>
                        <div className="text-sm text-muted">
                          To: {email.to} | {email.supplier}
                        </div>
                      </div>
                    ))
                  ) : (
                    // Single line PO - show full email preview
                    <>
                      <div className="text-sm" style={{ marginBottom: '8px' }}>
                        <strong>To:</strong> {pendingEmail.to}
                      </div>
                      <div className="text-sm" style={{ marginBottom: '12px' }}>
                        <strong>Subject:</strong> {pendingEmail.subject}
                      </div>
                      <div style={{ 
                        background: 'var(--bg-secondary)', 
                        padding: '12px', 
                        borderRadius: 'var(--radius)',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        maxHeight: '200px',
                        overflow: 'auto'
                      }}>
                        {pendingEmail.body}
                      </div>
                    </>
                  )}
                </div>

                {/* Action Buttons */}
                <div style={{ 
                  padding: '12px 16px', 
                  borderTop: '1px solid var(--border)',
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap'
                }}>
                  {/* Copy to clipboard */}
                  <button 
                    className="btn btn-secondary btn-sm"
                    onClick={handleCopyEmail}
                  >
                    <Copy size={14} />
                    {copySuccess ? 'Copied!' : `Copy ${pendingEmail.emails ? 'All ' : ''}to Clipboard`}
                  </button>

                  {/* Confirm sent (manual) */}
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => handleConfirmSent('manual')}
                    disabled={sendingEmail}
                  >
                    <Check size={14} />
                    {sendingEmail ? 'Confirming...' : 'Confirm Sent'}
                  </button>

                  {/* Send via Email (if configured) */}
                  {emailConfigured && (
                    <button 
                      className="btn btn-outline btn-sm"
                      onClick={handleSendViaAPI}
                      disabled={sendingEmail}
                      style={{ borderColor: '#0078d4', color: '#0078d4' }}
                    >
                      <Mail size={14} />
                      {sendingEmail ? 'Sending...' : `Send via ${emailProvider || 'Email'}`}
                    </button>
                  )}
                  
                  {/* Cancel */}
                  <button 
                    className="btn btn-outline btn-sm"
                    onClick={handleCancelEmail}
                    disabled={sendingEmail}
                  >
                    <X size={14} />
                    Cancel
                  </button>
                </div>

                {/* Info message */}
                <div style={{ 
                  padding: '12px 16px', 
                  background: emailConfigured ? '#d4edda' : '#fff3cd',
                  borderTop: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px'
                }}>
                  <AlertTriangle size={18} style={{ color: emailConfigured ? '#155724' : '#856404', flexShrink: 0, marginTop: '2px' }} />
                  <div style={{ fontSize: '0.875rem', color: emailConfigured ? '#155724' : '#856404' }}>
                    {emailConfigured ? (
                      <>
                        <strong>{emailProvider || 'Email'} configured.</strong><br />
                        Click "Send via {emailProvider || 'Email'}" to send automatically, or "Copy to Clipboard" then "Confirm Sent" if sending manually.
                      </>
                    ) : (
                      <>
                        <strong>Email not configured.</strong><br />
                        Copy the email content, send manually, then click "Confirm Sent" to update status to DAY1_SENT.
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {sending && (
            <div className="chat-message assistant">
              <div className="message-bubble">
                <div className="flex items-center gap-2">
                  <div className="spinner" style={{ width: '16px', height: '16px' }} />
                  <span>Thinking...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={sendMessage} className="chat-input-container">
          <textarea
            className="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage(e);
              }
            }}
            placeholder="Type your message..."
            rows={1}
          />
          <button type="submit" className="btn btn-primary" disabled={!input.trim() || sending}>
            <Send size={18} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default Chat;